<script setup>
/**
 * O resumo da carteira, no topo de Contatos (2026-08-06).
 *
 * Responde duas perguntas que a lista sozinha não responde:
 *   1. Quantos dos meus contatos estão VIVOS? (ativos / total)
 *   2. De onde eles vieram? (ranking dos números da conta)
 *
 * ── Por que barras, e não pizza ─────────────────────────────────────────────────────
 * A origem é parte-do-todo, o que em tese abriria espaço para pizza. Mas a pergunta aqui
 * é de ORDEM — "qual número mais recebe" — e comparar 6 fatias de tamanho parecido num
 * círculo é justamente o que o olho não faz bem. Barras ordenadas por tamanho respondem
 * de relance, e a soma continua fechando em 100%. (A conversa de formato por tipo de dado
 * fica para o Dashboard.)
 *
 * ── Cor ─────────────────────────────────────────────────────────────────────────────
 * Dado é monocromático: preenchimento em ink, trilho em neutro. Sem verde, sem vermelho,
 * sem categórico por matiz — a diferença entre as barras é comprimento, que é a
 * codificação que o olho lê com precisão.
 *
 * ── "Sem origem" ────────────────────────────────────────────────────────────────────
 * Só aparece quando existe. É a linha honesta: contato cadastrado à mão, ou de uma caixa
 * fora do alcance de quem lê. Sem ela o ranking não fecharia com o total, e um número que
 * não bate é pior que um número a mais.
 */
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useContactsResumo } from 'dashboard/composables/cw_custom/useContactOrigins.js';
// custom (2026-08-08): barra única repartida + legenda, no lugar da fileira de barras.
import CrmComposicao from 'dashboard/routes/dashboard/cw_custom/CrmComposicao.vue';

const props = defineProps({
  /**
   * Qual recorte da carteira este painel descreve: `'todos'` ou `'ativos'`.
   * ⚠️ Existe desde 2026-08-08 e conserta uma incoerência real: o painel SEMPRE desenhava o
   * ranking de origem do universo inteiro, mesmo quando o número ao lado dizia "ativos". O
   * Gustavo leu na tela — "quando eu entro em Todos os Contatos aparece um gráfico de
   * Contatos Ativos, está mal definido". Agora o gráfico segue o filtro.
   */
  escopo: {
    type: String,
    default: 'todos',
    validator: v => ['todos', 'ativos'].includes(v),
  },
});

const accountId = useMapGetter('getCurrentAccountId');
// Estado compartilhado com o bloco da tela inicial: uma leitura, uma verdade.
const { resumo, carregando } = useContactsResumo(accountId);

const total = computed(() => resumo.value?.total ?? 0);
const ativos = computed(() => resumo.value?.ativos ?? 0);
const janelaDias = computed(() => resumo.value?.janela_dias ?? 30);

const soAtivos = computed(() => props.escopo === 'ativos');

// O número que o painel exibe grande, e o denominador dele. Em "ativos" não há denominador:
// o recorte JÁ é o universo da tela, e "12 de 12" seria uma fração que não informa nada.
const valorPrincipal = computed(() =>
  soAtivos.value ? ativos.value : total.value
);
const rotuloPrincipal = computed(() =>
  soAtivos.value ? 'Contatos ativos' : 'Contatos'
);

const percentualAtivos = computed(() =>
  total.value ? Math.round((ativos.value / total.value) * 100) : 0
);

const ativosFormatado = computed(() => ativos.value.toLocaleString('pt-BR'));

// O ranking + a linha "sem origem". O comprimento da barra é medido contra o MAIOR valor,
// não contra o total: com 6 números bem distribuídos, barras medidas contra o total
// ficariam todas curtas e a comparação — que é o ponto — sumiria. O percentual ao lado
// continua sendo sobre o total, que é o número que a pessoa quer citar.
// ⚠️ O RANKING SEGUE O ESCOPO. O backend manda os dois recortes na mesma resposta
// (`por_caixa` e `por_caixa_ativos`), então trocar o filtro não dispara requisição nem faz o
// gráfico piscar. O denominador do percentual troca junto — percentual de ativos sobre o
// total de TODOS seria um número que não fecha em 100%.
const linhas = computed(() => {
  if (!resumo.value) return [];

  const origem = soAtivos.value
    ? resumo.value.por_caixa_ativos
    : resumo.value.por_caixa;
  const semOrigem = soAtivos.value
    ? resumo.value.sem_origem_ativos
    : resumo.value.sem_origem;
  const denominador = soAtivos.value ? ativos.value : total.value;

  const base = [...(origem ?? [])];
  if (semOrigem > 0) {
    base.push({
      inbox_id: null,
      nome: 'Sem origem',
      contatos: semOrigem,
    });
  }

  const maior = base.reduce((max, l) => Math.max(max, l.contatos), 0) || 1;
  return base.map(linha => ({
    ...linha,
    contatosFormatado: linha.contatos.toLocaleString('pt-BR'),
    percentual: denominador
      ? Math.round((linha.contatos / denominador) * 100)
      : 0,
    larguraBarra: Math.max(2, Math.round((linha.contatos / maior) * 100)),
  }));
});

const temDados = computed(() => total.value > 0);

// Curta de propósito: na coluna de 256px a versão longa quebrava em duas linhas e
// empurrava a barra para longe do número que ela mede.
const legendaAtivos = computed(
  () =>
    `${percentualAtivos.value}% trocaram mensagem em ${janelaDias.value} dias`
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div
    v-if="!carregando && temDados"
    class="flex flex-col gap-6 px-6 py-5 border rounded-card lg:flex-row lg:gap-10 border-n-weak bg-n-solid-1"
  >
    <!-- O número do RECORTE. Em "todos" ele é o tamanho da carteira, com a barra dizendo
         que fatia dela está viva; em "ativos" ele é o próprio universo da tela, e aí não há
         fração a mostrar — a barra sairia sempre cheia, o que não informa nada. -->
    <div class="flex flex-col gap-2 lg:w-64 shrink-0">
      <span class="text-mj-15 font-500 text-n-slate-11">
        {{ rotuloPrincipal }}
      </span>
      <div class="flex items-baseline gap-1.5">
        <span class="text-mj-34 font-500 tabular-nums text-n-slate-12">
          {{ valorPrincipal.toLocaleString('pt-BR') }}
        </span>
        <span v-if="!soAtivos" class="text-mj-15 tabular-nums text-n-slate-10">
          · {{ ativosFormatado }} ativos
        </span>
      </div>
      <div
        v-if="!soAtivos"
        class="h-1.5 overflow-hidden rounded-full bg-n-slate-4"
        role="img"
        :aria-label="legendaAtivos"
      >
        <div
          class="h-full rounded-full bg-n-slate-12"
          :style="{ width: `${percentualAtivos}%` }"
        />
      </div>
      <span class="text-mj-15 text-n-slate-11">
        {{
          soAtivos
            ? `Trocaram mensagem nos últimos ${janelaDias} dias`
            : legendaAtivos
        }}
      </span>
    </div>

    <!-- Ranking dos números -->
    <!-- A faixa é larga; as barras, não. Barra esticada pela largura da tela vira um bloco
         de tinta que esconde a comparação em vez de mostrá-la. -->
    <div
      v-if="linhas.length"
      class="flex flex-col flex-1 min-w-0 gap-2 max-w-[560px]"
    >
      <!-- O rótulo diz de QUE população o gráfico fala. Sem isso, os dois recortes desenham
           barras parecidas e nada na tela explica por que os números mudaram. -->
      <span class="text-mj-15 font-500 text-n-slate-11">
        {{ soAtivos ? 'De onde vieram os ativos' : 'De onde vieram' }}
      </span>
      <!-- custom (2026-08-08): fileira de barras pretas → composição colorida. Era o último
           lugar do produto com esse desenho ("todos os contatos estão em preto, deve ficar
           no colorido também"). Mesmo componente das outras telas — ver CrmComposicao.vue
           para o racional de por que composição e não ranking. -->
      <CrmComposicao
        :itens="linhas.map(l => ({ label: l.nome, valor: l.contatos }))"
      />
    </div>
  </div>
</template>
